<template>
  <div ref="TableViewer" class="table-viewer">
    <div ref="TableContent" class="rows-container">
      <div
        v-for="(row, rowIndex) in displayRows"
        :key="rowIndex"
        class="table-row"
        :style="getRowStyle(row, rowIndex)"
      >
        <div
          v-for="(cell, cellIndex) in row.data"
          :key="cellIndex"
          class="table-col"
          :style="getCellStyle(cell, rowIndex, cellIndex)"
        >
          <div class="cell-content-wrapper">
            <span class="cell-content">{{ cell.content }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="showPagination && totalPages > 1"
      class="pagination-container"
      :style="paginationStyle"
    >
      <template v-if="!paginationInText">
        <div v-for="index in totalPages" :key="index" class="page-dot">
          <div
            :class="index === currentPage ? 'current-dot' : 'dot'"
            :style="{
              backgroundColor: index === currentPage ? paginationFontColor : 'auto',
            }"
          ></div>
        </div>
      </template>
      <div v-else class="pagination-in-text">Page {{ currentPage }}/{{ totalPages }}</div>
    </div>
  </div>
</template>

<script>
  import { getRBGAColor, hex8ToRgba, isHex8 } from '@/helpers/utils';
  import { getCellProperty, getBorderProperty } from '@/helpers/tableCellProperties';

  export default {
    name: 'TableViewer',

    props: {
      settings: {
        type: Object,
        required: true,
      },
      height: {
        type: Number,
        default: null,
      },
    },

    data() {
      return {
        rowsContainerHeight: '100%',
        currentPageIndex: 0,
        timer: null,
      };
    },

    mounted() {
      this.$nextTick(() => {
        this.calculateRowHeight();
        this.startPagination();
      });
    },

    beforeDestroy() {
      this.destroyTimer();
    },

    watch: {
      'settings.pages': {
        handler() {
          this.$nextTick(this.calculateRowHeight);
        },
        deep: true,
      },
      currentPageIndex() {
        this.$nextTick(this.calculateRowHeight);
      },
      height() {
        this.$nextTick(this.calculateRowHeight);
      },
    },

    computed: {
      tableStyles() {
        return this.settings.styles || {};
      },

      tableConfig() {
        return this.tableStyles.table || {};
      },

      paginationConfig() {
        return this.tableStyles.pagination || {};
      },

      tableTheme() {
        return this.tableConfig.theme || '';
      },

      totalPages() {
        return this.settings.pages ? this.settings.pages.length : 1;
      },

      currentPageData() {
        const pages = this.settings.pages || [];
        return pages[this.currentPageIndex] || { columns: [], rows: [] };
      },

      currentRows() {
        return this.currentPageData.rows || [];
      },

      currentColumns() {
        return this.currentPageData.columns || [];
      },

      displayRows() {
        return this.currentRows;
      },

      currentPage() {
        return this.currentPageIndex + 1;
      },

      paginationFont() {
        return this.paginationConfig.font || 'Arial';
      },

      paginationFontSize() {
        return this.paginationConfig.fontSize || 14;
      },

      paginationFontColor() {
        return isHex8(this.paginationConfig.fontColor)
          ? hex8ToRgba(this.paginationConfig.fontColor)
          : this.paginationConfig.fontColor || '#000000';
      },

      paginationAlignment() {
        return this.paginationConfig.alignment || 'center';
      },

      paginationInText() {
        return this.paginationConfig.inText || false;
      },

      showPagination() {
        return this.paginationConfig.show === true;
      },

      paginationStyle() {
        const verticalPadding = this.paginationConfig.verticalPadding || 8;
        return {
          fontFamily: this.paginationFont,
          fontSize: this.paginationFontSize + 'px',
          lineHeight: this.paginationFontSize * 1.2 + 'px',
          color: this.paginationFontColor,
          justifyContent: this.paginationAlignment,
          paddingTop: verticalPadding + 'px',
          paddingBottom: verticalPadding + 'px',
          height: this.paginationInText
            ? this.paginationFontSize + verticalPadding * 2 + 'px'
            : 'auto',
        };
      },

      tableBorderStyle() {
        const border = this.tableConfig.border;

        if (!border || !border.width) return 'none';

        const borderColor = isHex8(border.color) ? hex8ToRgba(border.color) : border.color;

        return `${border.width}px solid ${borderColor}`;
      },

      tableStyle() {
        return {
          backgroundColor: getRBGAColor(this.tableConfig.backgroundColor || '#FFFFFF'),
          border: this.tableBorderStyle,
        };
      },
    },

    methods: {
      calculateRowHeight() {
        if (!this.$refs.TableContent) return;

        const totalRows = this.currentRows.length;
        if (totalRows === 0) return;

        const containerHeight = this.height || this.$refs.TableContent.clientHeight;
        this.rowsContainerHeight = containerHeight / totalRows + 'px';
      },

      getRowStyle(row, rowIndex) {
        // Use percentage-based height, fallback to equal distribution
        const heightPercent =
          row && row.heightPercent != null ? row.heightPercent : 100 / this.currentRows.length;

        return {
          height: heightPercent + '%',
          borderBottomColor: this.tableConfig.linesColor,
          borderBottomWidth: (this.tableConfig.lineWidth || 1) + 'px',
        };
      },

      getCellStyle(cell, rowIndex, cellIndex) {
        const column = this.currentColumns[cellIndex];

        // Use percentage-based width, fallback to equal distribution
        const widthPercent =
          column && column.widthPercent != null
            ? column.widthPercent
            : 100 / this.currentColumns.length;

        const fontSize = getCellProperty(cell, 'fontSize') || 16;
        const fontFamily = getCellProperty(cell, 'fontFamily') || 'Arial';
        const fontColor = getCellProperty(cell, 'fontColor') || '#000000';
        const backgroundColor = getCellProperty(cell, 'backgroundColor') || 'transparent';
        const textAlign = getCellProperty(cell, 'textAlign') || 'center';
        const verticalAlign = getCellProperty(cell, 'verticalAlign') || 'middle';
        const lineHeight = getCellProperty(cell, 'lineHeight') || 1.2;
        const fontWeight = getCellProperty(cell, 'fontWeight') || 400;
        const fontStyle = getCellProperty(cell, 'fontStyle') || 'normal';
        const textDecoration = getCellProperty(cell, 'textDecoration') || 'none';
        const paddingTop =
          getCellProperty(cell, 'paddingTop') != null ? getCellProperty(cell, 'paddingTop') : 0;
        const paddingRight =
          getCellProperty(cell, 'paddingRight') != null ? getCellProperty(cell, 'paddingRight') : 0;
        const paddingBottom =
          getCellProperty(cell, 'paddingBottom') != null
            ? getCellProperty(cell, 'paddingBottom')
            : 0;
        const paddingLeft =
          getCellProperty(cell, 'paddingLeft') != null ? getCellProperty(cell, 'paddingLeft') : 0;

        const bgColor = isHex8(backgroundColor) ? hex8ToRgba(backgroundColor) : backgroundColor;
        const fColor = isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor;

        const style = {
          width: widthPercent + '%',
          fontSize: fontSize + 'px',
          fontFamily: fontFamily,
          color: fColor,
          backgroundColor: bgColor,
          textAlign: textAlign,
          justifyContent: this.getJustifyContent(verticalAlign),
          lineHeight: lineHeight,
          fontWeight: fontWeight,
          fontStyle: fontStyle,
          textDecoration: textDecoration,
          paddingTop: paddingTop + 'px',
          paddingRight: paddingRight + 'px',
          paddingBottom: paddingBottom + 'px',
          paddingLeft: paddingLeft + 'px',
        };

        const borderTop = getBorderProperty(cell, 'borderTop');
        const borderRight = getBorderProperty(cell, 'borderRight');
        const borderBottom = getBorderProperty(cell, 'borderBottom');
        const borderLeft = getBorderProperty(cell, 'borderLeft');

        if (borderTop) {
          const borderTopColor = isHex8(borderTop.color)
            ? hex8ToRgba(borderTop.color)
            : borderTop.color;
          style.borderTop = `${borderTop.width}px solid ${borderTopColor}`;
        }
        if (borderRight) {
          const borderRightColor = isHex8(borderRight.color)
            ? hex8ToRgba(borderRight.color)
            : borderRight.color;
          style.borderRight = `${borderRight.width}px solid ${borderRightColor}`;
        }
        if (borderBottom) {
          const borderBottomColor = isHex8(borderBottom.color)
            ? hex8ToRgba(borderBottom.color)
            : borderBottom.color;
          style.borderBottom = `${borderBottom.width}px solid ${borderBottomColor}`;
        }
        if (borderLeft) {
          const borderLeftColor = isHex8(borderLeft.color)
            ? hex8ToRgba(borderLeft.color)
            : borderLeft.color;
          style.borderLeft = `${borderLeft.width}px solid ${borderLeftColor}`;
        }

        return style;
      },

      getJustifyContent(verticalAlign) {
        switch (verticalAlign) {
          case 'top':
            return 'flex-start';
          case 'bottom':
            return 'flex-end';
          default:
            return 'center';
        }
      },

      startPagination() {
        if (this.totalPages <= 1) return;
        this.initTimer();
      },

      initTimer() {
        const interval = this.paginationConfig.interval || 5;
        this.timer = setInterval(this.nextPage, interval * 1000);
      },

      destroyTimer() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      },

      nextPage() {
        this.currentPageIndex =
          this.currentPageIndex < this.totalPages - 1 ? this.currentPageIndex + 1 : 0;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .table-viewer {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    flex-grow: 1;
    overflow: hidden;

    .rows-container {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      flex-grow: 1;
      height: 100%;

      .table-row {
        display: flex;
        width: 100%;

        .table-col {
          display: flex;
          flex-direction: column;
          border-right: none;
          box-sizing: border-box;
          position: relative;
          overflow: visible;
          word-wrap: break-word;
          word-break: break-word;
          overflow-wrap: break-word;
          -webkit-flex-shrink: 0;
          flex-shrink: 0;
          -webkit-flex-grow: 0;
          flex-grow: 0;
          min-width: 0;

          .cell-content-wrapper {
            width: 100%;
            height: 100%;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            -webkit-justify-content: inherit;
            justify-content: inherit;
            white-space: pre-wrap;
            word-wrap: break-word;
          }

          .cell-content {
            white-space: pre-wrap;
            word-wrap: break-word;
          }
        }
      }
    }
  }

  .pagination-container {
    display: flex;
    -webkit-align-items: center;
    align-items: center;
    -webkit-justify-content: center;
    justify-content: center;
    padding-left: 20px;
    padding-right: 20px;
    width: 100%;
    -webkit-flex-wrap: wrap;
    flex-wrap: wrap;

    .page-dot {
      display: flex;
      -webkit-align-items: center;
      align-items: center;
      -webkit-justify-content: center;
      justify-content: center;
      width: 34px;
      height: 20px;
      border-radius: 100%;
      padding: 3px 10px;
    }

    .dot,
    .current-dot {
      background-color: #333333;
      border-radius: 100%;
    }

    .dot {
      opacity: 0.2;
      width: 8px;
      height: 8px;
    }

    .current-dot {
      width: 14px;
      height: 14px;
    }

    .pagination-in-text {
      font-weight: 500;
    }
  }
</style>
